import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Psychology,
  Assignment,
  FamilyRestroom,
  GppGood,
  Insights,
  Groups,
  Description,
  WhatsApp,
  CheckCircle,
} from '@mui/icons-material';
import PageContainer from 'src/pages/components/PageContainer';
import { isUserCompany } from 'src/utils/auth';

const WHATSAPP_NUMBER = '5519998449211';
const WHATSAPP_MESSAGE =
  'Olá, tenho interesse em conhecer os módulos do EezyOne para minha empresa. Gostaria de receber mais informações.';

type ModuleCard = {
  icon: JSX.Element;
  title: string;
  tagline: string;
  description: string;
  highlights: string[];
};

const MODULES: ModuleCard[] = [
  {
    icon: <Psychology color="primary" />,
    title: 'Saúde mental corporativa',
    tagline: 'Cuidado psicológico contínuo',
    description:
      'Atendimentos com psicólogos qualificados para os colaboradores, com agendamento, acompanhamento e sigilo clínico.',
    highlights: [
      'Rede de psicólogos e agendamento',
      'Prontuário e histórico clínico protegidos',
      'Atendimentos individuais e emergenciais',
    ],
  },
  {
    icon: <Assignment color="primary" />,
    title: 'Riscos psicossociais (NR-1)',
    tagline: 'Conformidade e diagnóstico',
    description:
      'Avaliações psicossociais para mapear riscos, apoiar o cumprimento da NR-1 e orientar planos de ação.',
    highlights: [
      'Avaliações e questionários estruturados',
      'Indicadores por setor e por período',
      'Apoio à conformidade trabalhista',
    ],
  },
  {
    icon: <FamilyRestroom color="primary" />,
    title: 'EezyFamily',
    tagline: 'Apoio que vai além do colaborador',
    description:
      'Estende o cuidado à rede de apoio do colaborador, ampliando o impacto do programa de bem-estar.',
    highlights: [
      'Acesso ampliado ao cuidado',
      'Portal dedicado a colaboradores',
      'Comunicação e notificações',
    ],
  },
  {
    icon: <GppGood color="primary" />,
    title: 'EezyLine',
    tagline: 'Canal de denúncias',
    description:
      'Canal seguro e confidencial para denúncias de assédio, discriminação, fraude e outras condutas — com proteção ao denunciante.',
    highlights: [
      'Formulário público anônimo ou identificado',
      'Gestão, timeline e planos de ação',
      'Apoio da Eezycare em consultoria',
    ],
  },
  {
    icon: <Insights color="primary" />,
    title: 'Dashboards e indicadores',
    tagline: 'Decisão baseada em dados',
    description:
      'Painéis de engajamento, adesão e evolução do programa para acompanhar resultados em tempo real.',
    highlights: [
      'Indicadores de engajamento',
      'Evolução por período e por setor',
      'Relatórios exportáveis',
    ],
  },
  {
    icon: <Groups color="primary" />,
    title: 'Gestão de colaboradores',
    tagline: 'Operação simplificada',
    description:
      'Cadastro e gestão de colaboradores, atendimentos e cotas em um só lugar, integrado a toda a plataforma.',
    highlights: [
      'Cadastro individual e em massa',
      'Gestão de atendimentos e cotas',
      'Resumo financeiro da empresa',
    ],
  },
];

const BENEFITS = [
  'Uma única plataforma para todo o programa de bem-estar',
  'Redução de riscos psicossociais e apoio à conformidade',
  'Mais engajamento e retenção de talentos',
  'Confidencialidade e segurança de dados (LGPD)',
];

export default function EezyOneLanding(): JSX.Element {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
    WHATSAPP_MESSAGE
  )}`;

  return (
    <PageContainer title="EezyOne" guard={isUserCompany}>
      <Box display="flex" flexDirection="column" gap={4}>
        <Stack alignItems="center" spacing={1.5} textAlign="center">
          <Chip label="Eezycare" color="success" size="small" />
          <Typography variant="h4" fontWeight={800}>
            EezyOne — o ecossistema de saúde e segurança psicossocial
          </Typography>
          <Typography color="text.secondary" maxWidth={720}>
            Uma plataforma única que conecta cuidado em saúde mental, conformidade,
            canal de denúncias e indicadores. Ative os módulos do EezyOne e leve o
            programa de bem-estar da sua empresa para o próximo nível.
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="success"
            startIcon={<WhatsApp />}
            component="a"
            href={whatsappUrl}
            target="_blank"
            rel="noreferrer"
          >
            Falar com um especialista
          </Button>
        </Stack>

        <Divider>
          <Typography variant="overline" color="text.secondary">
            Módulos do EezyOne
          </Typography>
        </Divider>

        <Grid container spacing={2}>
          {MODULES.map((m) => (
            <Grid key={m.title} item xs={12} sm={6} md={4}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {m.icon}
                      <Typography variant="subtitle1" fontWeight={700}>
                        {m.title}
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="success.main" fontWeight={600}>
                      {m.tagline}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {m.description}
                    </Typography>
                    <Stack spacing={0.5} mt={0.5}>
                      {m.highlights.map((h) => (
                        <Stack key={h} direction="row" spacing={0.5} alignItems="center">
                          <CheckCircle sx={{ fontSize: 16 }} color="success" />
                          <Typography variant="body2">{h}</Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card variant="outlined" sx={{ bgcolor: 'action.hover' }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography variant="h6" gutterBottom>
                  Por que o EezyOne?
                </Typography>
                <Stack spacing={0.5}>
                  {BENEFITS.map((b) => (
                    <Stack key={b} direction="row" spacing={1} alignItems="center">
                      <CheckCircle color="success" sx={{ fontSize: 18 }} />
                      <Typography variant="body2">{b}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Grid>
              <Grid item xs={12} md={5} textAlign={{ xs: 'left', md: 'center' }}>
                <Description color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Sua empresa já conta com um dos nossos módulos. Converse com nosso time
                  para desbloquear o ecossistema completo.
                </Typography>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<WhatsApp />}
                  component="a"
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Quero conhecer o EezyOne
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    </PageContainer>
  );
}
